// #614, Session 79.22b -- gate 371 addendum D38-D40, APPROVED BY COLBY, Sep 21:
// "i approve D38, 39, & 40 on gate 371."
//
// Jennifer is ordering Dallas's approved request and types Houston - one of our
// own branches. The signpost's button opens THIS, inside the same ordering
// sheet: what goes, why nobody at Dallas is asked again, who will be told, and
// what stays behind. Two presses from the signpost to sent.
//
// A top-level component in its own file (locked rule 2), so it can be tested
// without the 900 lines of sheet around it.

import React from 'react';
import ActionButton from '../ActionButton';
import InlineMessage from '../InlineMessage';
import { useTier, controlHeight } from '../../lib/tiers';
import { isValidTransferWorkOrder } from '../../lib/branchTransfers';

function dayLabel(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function lineName(it) {
  return it.is_off_catalog ? (it.off_catalog_description || 'An off-catalog line') : (it.inventory_items?.name || 'An item');
}

function lineUom(it) {
  return it.is_off_catalog ? (it.off_catalog_uom || '') : (it.inventory_items?.uom || '');
}

// Who the panel promises will be told - D40: the person who asked and the
// manager who approved, one sentence when they are the same person. A promise
// the system cannot keep is worse than not offering it, so it names exactly the
// two people sendRequestAsTransfer() tells.
export function whoIsTold(req) {
  const asker = req?.users?.full_name || null;
  const approver = req?.approver?.full_name || null;
  const same = req?.approved_by && req?.requested_by && String(req.approved_by) === String(req.requested_by);
  if (asker && (same || !approver)) {
    return same ? `${asker}, who asked for it and approved it, will be told.` : `${asker}, who asked for it, will be told.`;
  }
  if (asker && approver) return `${asker}, who asked for it, and ${approver}, who approved it, will be told.`;
  if (approver) return `${approver}, who approved it, will be told.`;
  return 'The person who asked for it will be told.';
}

export default function SendAsTransferPanel({
  req, checkedIds, supplierName, askingName, isMobile,
  busy = false, error = '', onClearError, onBack, onSend,
}) {
  const { coarse } = useTier();
  const commitH = controlHeight(coarse, 'commit');
  const minH = controlHeight(coarse, 'min');

  const items = req?.material_request_items || [];
  const ticked = new Set((checkedIds || []).map(String));
  const isTicked = it => ticked.has(String(it.id));
  const sendable = items.filter(it => isTicked(it) && !it.is_off_catalog && it.item_id != null);
  const offCatalog = items.filter(it => isTicked(it) && (it.is_off_catalog || it.item_id == null));
  const leftBehind = items.filter(it => !isTicked(it)).length;

  const approvedOn = req?.approved_by ? dayLabel(req.approved_at) : null;
  const woDropped = !!req?.work_order_ref && !isValidTransferWorkOrder(req.work_order_ref);
  const blockedReason = sendable.length > 0 ? null
    : (offCatalog.length > 0 ? 'Nothing ticked can go as a transfer — it is not in the catalog.' : 'Tick the lines to send.');

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 22px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#3A3A3A', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 7 }}>
        What goes to {supplierName}
      </div>
      <div style={{ border: '1px solid #E8E8E8', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
        {sendable.length === 0 ? (
          <div style={{ padding: '10px 12px', fontSize: 13, color: '#5C5C5C' }}>Nothing ticked yet.</div>
        ) : sendable.map((it, i) => (
          <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 12px', fontSize: 13, borderTop: i ? '1px solid #F0F0F0' : 'none' }}>
            <span style={{ color: '#0F0F0F', fontWeight: 600 }}>{lineName(it)}</span>
            <span style={{ color: '#0F0F0F', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {it.qty_requested}{lineUom(it) ? ` ${lineUom(it)}` : ''}
            </span>
          </div>
        ))}
      </div>

      {offCatalog.map(it => (
        <div key={it.id} style={{ background: '#FDF3E3', borderLeft: '3px solid #EF9F27', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: '#8A5A10', lineHeight: 1.45, marginBottom: 10 }}>
          &ldquo;{lineName(it)}&rdquo; is not in the catalog, so it cannot go as a transfer. It stays here, to order from a vendor.
        </div>
      ))}

      <div style={{ background: '#F1FAF6', border: '1px solid #CDEDDF', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#14503B', lineHeight: 1.45, marginBottom: 10 }}>
        {approvedOn
          ? `${askingName} approved this request on ${approvedOn}, so it goes straight to ${supplierName} to confirm. Nothing is bought.`
          : `No approval is recorded on this request, so ${askingName} agrees first, then ${supplierName} confirms. Nothing is bought.`}
      </div>

      {woDropped && (
        <div style={{ fontSize: 12.5, color: '#5C5C5C', lineHeight: 1.45, marginBottom: 8 }}>
          The work order on this request, &ldquo;{req.work_order_ref}&rdquo;, is not a valid number, so the transfer goes without it.
        </div>
      )}
      <div style={{ fontSize: 12.5, color: '#5C5C5C', lineHeight: 1.45, marginBottom: 8 }}>{whoIsTold(req)}</div>
      {leftBehind > 0 && (
        <div style={{ fontSize: 12.5, color: '#5C5C5C', lineHeight: 1.45, marginBottom: 8 }}>
          {leftBehind === 1
            ? '1 line you have not ticked stays here, to order from a vendor.'
            : `${leftBehind} lines you have not ticked stay here, to order from a vendor.`}
        </div>
      )}

      <InlineMessage type="error" message={error} onDismiss={onClearError} style={{ marginTop: 6 }} />

      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row-reverse',
        alignItems: isMobile ? 'stretch' : 'center', gap: 10, marginTop: 14,
      }}>
        <ActionButton
          onClick={() => onSend(sendable.map(it => it.id))}
          blockedReason={blockedReason}
          busy={busy}
          busyLabel={'Sending…'}
          className="btn btn-primary"
          style={isMobile
            ? { width: '100%', justifyContent: 'center', minHeight: commitH }
            : { minWidth: 150, justifyContent: 'center', minHeight: commitH }}
        >
          Send to {supplierName}
        </ActionButton>
        <button onClick={onBack} disabled={busy} className="btn btn-ghost"
          style={isMobile
            ? { width: '100%', justifyContent: 'center', minHeight: minH }
            : { minWidth: 110, justifyContent: 'center', minHeight: minH }}>
          Back
        </button>
      </div>
    </div>
  );
}
